import { get } from 'lodash-es';
import type atlas from 'azure-maps-control';

import { getMilageInLatLong, logDevMessage } from 'utils/utils';
import { AVERAGE_CARD_HEIGHT, FLOATING_CARD_BOTTOM } from './mapConstants';
import type { MapBounds, Coordinates, MapCameraPosition } from './map.types';

/** Check's if the latitude and longitude passed are valid */
export function isValidCoords(latitude: number, longitude: number): boolean {
  if (typeof latitude !== 'number' || typeof longitude !== 'number') {
    return false;
  }

  return latitude >= -90 && latitude <= 90 && longitude >= -180 && longitude <= 180;
}

/**
 * This function takes the existing map bounds and returns a new set of bounds that
 * is re-framed around a set of coordinates, such that the coordinates are centered
 * horizontally, and placed vertically in the center of the *remaining* map space once
 * the area covered by the active result card or slider is taken into account.
 *
 * Center horizontally by taking the width of the original map in "degrees", then
 * creating a new pair of longitudal bounds that preserve this width, but centered
 * around the longitude value of the given coords.
 *
 * Center vertically by creating a new bounds-pair where the top and bottom latitude
 * are shifted by the same value in order to center the given coord in the remaining space.
 * See docs for 'getRelativeCenter' for more details.
 */
export function shiftBoundsForCoords({
  bounds,
  coords,
  relativeCenter = 0.8,
}: {
  bounds: MapBounds;
  coords: Coordinates;
  relativeCenter: number;
}): MapBounds {
  const { latitude: markerLat, longitude: markerLng } = coords;
  const { sw, ne } = bounds;

  // center horizontally
  const boundsWidth = ne.longitude - sw.longitude;
  const boundsWidthHalf = boundsWidth / 2;
  const leftLongitude = markerLng - boundsWidthHalf;
  const rightLongitude = markerLng + boundsWidthHalf;
  // center vertically (relative to space already taken by result card)
  const boundsHeight = ne.latitude - sw.latitude;
  const padMarker = boundsHeight * (1 - relativeCenter); // desired "top padding"
  const distanceFromMarkerToTopBounds = ne.latitude - markerLat; // initial "top padding"
  const verticalShift = distanceFromMarkerToTopBounds - padMarker; // adjustment to acheive desired padding

  return {
    ne: {
      latitude: ne.latitude - verticalShift,
      longitude: rightLongitude,
    },
    sw: {
      latitude: sw.latitude - verticalShift,
      longitude: leftLongitude,
    },
  };
}

/**
 * Accepts a boundsPair object (pre-validated) and returns a boundsPair object
 * that is zoomed to 95% of the original size. It does so by cropping 2.5% of the
 * total viewport width and height from each corner.
 */
export const zoomBounds = (bounds: MapBounds): MapBounds => {
  const { sw, ne } = bounds;
  const boundsWidth = ne.longitude - sw.longitude;
  const boundsLength = ne.longitude - sw.longitude;

  const widthPad = boundsWidth * 0.025;
  const lengthPad = boundsLength * 0.025;

  return {
    sw: {
      latitude: sw.latitude + widthPad,
      longitude: sw.longitude + lengthPad,
    },
    ne: {
      latitude: ne.latitude - widthPad,
      longitude: ne.longitude - lengthPad,
    },
  };
};

/**
 * Accepts the Azure maps object returned from evt.map.getCamera()
 * Returns a structured mapBounds object. Default to null
 */
export const getMapBounds = (
  mapCamera: atlas.CameraOptions & atlas.CameraBoundsOptions
): MapBounds | null => {
  if (!mapCamera) {
    logDevMessage('Cannot get map bounds - Azure maps camera object is not defined');
    return null;
  }
  const { bounds } = mapCamera;
  if (!bounds) return null;
  if (!isValidCoords(bounds[1], bounds[0]) || !isValidCoords(bounds[3], bounds[2])) return null;
  return {
    sw: {
      longitude: bounds[0],
      latitude: bounds[1],
    },
    ne: {
      longitude: bounds[2],
      latitude: bounds[3],
    },
  };
};

/**
 * The 'relativeCenter' represents the height from the bottom of the map that will mark
 * the center of the visible map space. For instance, if a result card is covering the
 * bottom 40% of the map, the remaining open map space will be 60%, so the relative center
 * will be .7, which is the distance from the bottom of the map to the middle of the open 60%
 */
export const getRelativeCenter = (mapHeight: number, coveredAreaHeight: number): number | null => {
  if (!mapHeight || !coveredAreaHeight) return null;
  const coveredHeightRatio = coveredAreaHeight / mapHeight;
  const openHeightRatio = 1 - coveredHeightRatio;
  const proportionalCenterOfOpenMapArea = coveredHeightRatio + openHeightRatio / 2;
  // this function should return a value between 0 and 1, otherwise default to null
  if (proportionalCenterOfOpenMapArea > 1 || proportionalCenterOfOpenMapArea < 0) return null;
  return proportionalCenterOfOpenMapArea;
};

/** Performs a series of computations to appropriately frame an active result. */
export const reframeBoundsToActiveMarker = ({
  bounds,
  latitude,
  longitude,
  mapHeight,
}: {
  bounds: MapBounds;
  latitude: number;
  longitude: number;
  mapHeight: number;
}): MapBounds | null => {
  const offsetHeight = AVERAGE_CARD_HEIGHT + FLOATING_CARD_BOTTOM;
  /**
   * get new bounds that center the marker in the remaining map
   * space accounting for the height of the floating result card
   * */
  const relativeCenter = getRelativeCenter(mapHeight, offsetHeight);
  if (!relativeCenter) return null;
  const shiftedBounds = shiftBoundsForCoords({
    bounds,
    coords: { latitude, longitude },
    relativeCenter,
  });

  /**
   * zoom in 2.5%. Otherwise the "fly" animation at the same zoom level can
   * re-combine broken clusters and result in a confusing map state
   * */
  const { ne, sw } = zoomBounds(shiftedBounds);

  // check results for validity
  if (!isValidCoords(ne.latitude, ne.longitude) || !isValidCoords(sw.latitude, sw.longitude)) {
    logDevMessage('Reframe bounds calculations failure');
    return null;
  }
  return { ne, sw };
};

/** This is a temporary interface created to satisfy type checks for getBounds.
 * @TODO TypeScript Migration: This should be replaced once we have developed a pattern for
 * using typing the provider and place results fetched from the API.
 */
interface _TempResultType {
  latitude?: number;
  longitude?: number;
  closestVisibleLocation?: {
    latitude: number;
    longitude: number;
  };
}

export function getBounds(
  results: _TempResultType[],
  mileage: number = 0.5,
  padding: number = 0.2
): [number, number, number, number] | undefined {
  if (
    results.length &&
    (!get(results[0], 'latitude') || !get(results[0], 'longitude')) &&
    (!get(results[0], 'closestVisibleLocation.latitude') ||
      !get(results[0], 'closestVisibleLocation.longitude'))
  ) {
    console.log(
      'Utls.js - results must be passed to getBounds() with valid latitude and longitude properties'
    );
    return;
  }

  if (!results.length) {
    console.log('Utls.js - no results passed to getBounds()');
    return;
  }
  const firstResultLatitude =
    get(results[0], 'latitude') && get(results[0], 'longitude')
      ? get(results[0], 'latitude')
      : get(results[0], 'closestVisibleLocation.latitude');

  const firstResultLongitude =
    get(results[0], 'latitude') && get(results[0], 'longitude')
      ? get(results[0], 'longitude')
      : get(results[0], 'closestVisibleLocation.longitude');

  if (results.length === 1) {
    if (!firstResultLatitude || !firstResultLongitude) return;
    // if there is only one result, we calculate and create buffer (.5 mile by default) on each side
    // calculations explained at bottom of this file
    const { mileageLat, mileageLng } = getMilageInLatLong(firstResultLatitude, mileage);

    // eslint-disable-next-line consistent-return
    return [
      firstResultLongitude - mileageLng,
      firstResultLatitude - mileageLat,
      firstResultLongitude + mileageLng,
      firstResultLatitude + mileageLat,
    ];
  }

  const neCoords = {
    longitude: -Infinity,
    latitude: -Infinity,
  };
  const swCoords = {
    longitude: Infinity,
    latitude: Infinity,
  };

  if (results.length) {
    results.forEach((result) => {
      const lat =
        result.latitude && result.longitude
          ? result.latitude
          : get(result, 'closestVisibleLocation.latitude');

      const lng =
        result.latitude && result.longitude
          ? result.longitude
          : get(result, 'closestVisibleLocation.longitude');

      if (!lat || !lng) return;

      if (neCoords.latitude < lat) {
        neCoords.latitude = lat;
      }
      if (neCoords.longitude < lng) {
        neCoords.longitude = lng;
      }
      if (swCoords.latitude > lat) {
        swCoords.latitude = lat;
      }
      if (swCoords.longitude > lng) {
        swCoords.longitude = lng;
      }
    });
  }

  // The above code calculates the exact bounds of the coords on the map. The below code
  // adds a little padding to those coords - so markers don't get cut off
  // The padding is set proportionally to the height and width of the bounding box
  // e.g. An icon whos coords are at the very top of the map will be cut off - because the icons anchor is 'bottom' and the icon will extend above the map

  const width = Math.abs(neCoords.longitude - swCoords.longitude);
  const height = Math.abs(neCoords.latitude - swCoords.latitude);

  padding = padding < 1 && padding > 0 ? padding : 0.2;

  swCoords.longitude -= width * padding;
  swCoords.latitude -= height * padding;
  neCoords.longitude += width * padding;
  neCoords.latitude += height * padding;

  // format for mapbox
  const swCoordsArray: [number, number] = [swCoords.longitude, swCoords.latitude];
  const neCoordsArray: [number, number] = [neCoords.longitude, neCoords.latitude];
  // eslint-disable-next-line consistent-return
  return [...swCoordsArray, ...neCoordsArray];
}

/** Returns the coordinates for sw, ne, center amd the zoom level. */
export function getValuesFromMapRef(map: atlas.Map): MapCameraPosition | null {
  const { bounds, center, zoom } = map.getCamera();
  if (!bounds || !center || zoom === undefined) return null;

  const [swLng, swLat, neLng, neLat] = bounds;
  const [centerLng, centerLat] = center;

  const mapValues: MapCameraPosition | null = {
    center: { latitude: centerLat, longitude: centerLng },
    sw: { latitude: swLat, longitude: swLng },
    ne: { latitude: neLat, longitude: neLng },
    zoom,
  };
  return mapValues;
}

/** Accepts a list of coordinates and returns the minimum size bounding box that contains all the coordinates */
export function getBoundingBoxFromCoordinatesList(
  coordsList: Coordinates[] = []
): MapBounds | null {
  if (!Array.isArray(coordsList)) {
    logDevMessage('getBoundsFromCoordinatesList expects an array of coordinates');
    return null;
  }

  if (!coordsList.length) {
    logDevMessage('getBoundsFromCoordinatesList requires at least 1 coordinate');
    return null;
  }

  let minLat = Infinity;
  let maxLat = -Infinity;
  let minLng = Infinity;
  let maxLng = -Infinity;

  // iterate over coordinates to set the max and min lat/lng
  for (const coord of coordsList) {
    // max and min lat
    if (coord.latitude < minLat) minLat = coord.latitude;
    if (coord.latitude > maxLat) maxLat = coord.latitude;
    // max and min lng
    if (coord.longitude < minLng) minLng = coord.longitude;
    if (coord.longitude > maxLng) maxLng = coord.longitude;
  }

  // return the sw corner and ne corner bounds
  return {
    sw: {
      latitude: minLat,
      longitude: minLng,
    },
    ne: {
      latitude: maxLat,
      longitude: maxLng,
    },
  };
}

/** Accepts two coordinates objects and returns a string in the format neLat,neLng__swLat,swLng or an empty string if no valid coords */
export const getBoundsAsQueryParams = (sw: Coordinates, ne: Coordinates): string => {
  if (isValidCoords(sw?.latitude, sw?.longitude) && isValidCoords(ne?.latitude, ne?.longitude)) {
    return encodeURIComponent(`${ne.latitude},${ne.longitude}__${sw.latitude},${sw.longitude}`);
  }
  return '';
};
